// Building blocks for the new screens: plain white pages, a left menu, simple tables and forms,
// totals right-aligned (QuickBooks / Xero style). Primary actions use the site's own colour.
import { Link } from "react-router-dom";
import { kes } from "@/lib/format";

export const money = (v) => kes(Number(v) || 0);
export const signed = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${kes(Math.abs(Number(v) || 0))}`;
export const tone = (v, tol = 100) => (Math.abs(v) < 0.5 ? "text-emerald-700" : Math.abs(v) <= tol ? "text-amber-700" : "text-rose-700");

export function Page({ title, back, actions, children }) {
  return (
    <div className="mx-auto w-full max-w-[1280px] px-5 py-6 md:px-10 md:py-7">
      {back && <Link to={back.to} className="text-[13px] text-sky-700 hover:underline">← {back.label}</Link>}
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Btn({ kind = "secondary", className = "", ...p }) {
  const look = kind === "primary"
    ? "bg-brand text-white hover:bg-brand/90 border-transparent font-semibold"
    : kind === "outline"
      ? "bg-white text-brand border-brand font-semibold hover:bg-slate-50"
      : kind === "link"
        ? "border-transparent bg-transparent px-0 font-semibold text-sky-700 hover:underline"
        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50";
  return <button type="button" className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-md border px-4 text-sm disabled:opacity-50 ${look} ${className}`} {...p} />;
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div role="tablist" className="mt-5 flex flex-wrap gap-6 border-b border-slate-200">
      {tabs.map((t) => (
        <button key={t.value} type="button" role="tab" aria-selected={value === t.value} onClick={() => onChange(t.value)}
          className={`min-h-[44px] border-b-2 py-2.5 text-sm ${value === t.value ? "border-brand font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Card({ title, children, className = "" }) {
  return (
    <section className={`rounded-lg border border-slate-200 p-5 ${className}`}>
      {title && <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>}
      {children}
    </section>
  );
}

// a table: cols = [{ label, align: "right"|"left", className }]
export function Table({ cols, children, foot, minWidth = 640 }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full border-collapse text-sm" style={{ minWidth }}>
        <thead>
          <tr className="border-b border-slate-200 text-xs text-slate-500">
            {cols.map((c, i) => (
              <th key={i} scope="col" className={`px-2 py-2 font-semibold ${c.align === "right" ? "text-right" : "text-left"} ${c.className || ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
        {foot && <tfoot className="font-semibold">{foot}</tfoot>}
      </table>
    </div>
  );
}
export const Tr = ({ children, className = "" }) => <tr className={`border-b border-slate-100 ${className}`}>{children}</tr>;
export const Td = ({ children, right, className = "", ...p }) => <td className={`px-2 py-2 ${right ? "text-right tabular-nums" : ""} ${className}`} {...p}>{children}</td>;

// a number box for tables and forms
export function NumIn({ value, onChange, disabled, width = "w-28", label, className = "" }) {
  return (
    <input aria-label={label} inputMode="decimal" disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className={`min-h-[34px] rounded-md border border-slate-300 px-2 text-right tabular-nums disabled:border-transparent disabled:bg-transparent disabled:text-slate-500 ${width} ${className}`} />
  );
}
export function TextIn({ value, onChange, disabled, width = "w-full", label, placeholder, className = "" }) {
  return (
    <input aria-label={label} disabled={disabled} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className={`min-h-[34px] rounded-md border border-slate-300 px-2 disabled:border-transparent disabled:bg-transparent ${width} ${className}`} />
  );
}
export function Field({ label, children }) {
  return <label className="flex flex-col gap-1 text-xs text-slate-500">{label}{children}</label>;
}

// a definition list of totals, the last row emphasised
export function Totals({ rows }) {
  return (
    <dl className="grid w-full max-w-[380px] grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-2.5 text-sm">
      {rows.map((r, i) => {
        const last = i === rows.length - 1;
        return [
          <dt key={`t${i}`} className={last ? "border-t-2 border-slate-900 pt-2.5 text-base font-semibold text-slate-900" : "text-slate-600"}>{r[0]}</dt>,
          <dd key={`d${i}`} className={`m-0 text-right tabular-nums ${last ? `border-t-2 border-slate-900 pt-2.5 text-base font-bold ${r[2] || ""}` : r[2] || ""}`}>{r[1]}</dd>,
        ];
      })}
    </dl>
  );
}

export function Pill({ children, kind = "grey" }) {
  const k = { amber: "bg-amber-100 text-amber-800", blue: "bg-sky-100 text-sky-800", green: "bg-emerald-100 text-emerald-800", grey: "bg-slate-100 text-slate-700", red: "bg-rose-100 text-rose-800" }[kind];
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${k}`}>{children}</span>;
}

export const STATUS_PILL = {
  draft: ["Draft", "amber"], submitted: ["Awaiting approval", "blue"], manager_reviewed: ["Awaiting approval", "blue"],
  approved: ["Approved", "green"], locked: ["Approved", "green"],
};
export function StatusPill({ status }) {
  const [label, kind] = STATUS_PILL[status] || [status, "grey"];
  return <Pill kind={kind}>{label}</Pill>;
}

export function Loading() {
  return <div className="py-24 text-center text-sm text-slate-400">Loading…</div>;
}
